import React, { useState, useEffect, useCallback, useContext, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory, Link, useLocation } from 'react-router-dom';
import { useContextMenu } from 'react-contexify';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { AppBar as MuiAppBar } from '@material-ui/core';
import Toolbar from '@material-ui/core/Toolbar';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Skeleton from '@material-ui/lab/Skeleton';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import EditIcon from '@material-ui/icons/Create';
import IconButton from '@material-ui/core/IconButton';

import UserSessionContext from 'context/UserSessionContext';
import * as ACTIONS from 'context/actions';
import useApi, { UPDATE_USER_ROUTE } from 'hooks/useApi';

import { extractTabProps } from 'utils/widget-helpers';
import SearchInput from 'components/SearchInput';

const useStyles = makeStyles(theme => ({
  appRoot: {
    boxShadow: 'unset'
  },
  changeViewLabel: {
    '& > span:last-child': {
      color: theme.palette.primary.main,
      fontWeight: 'bold'
    }
  },
  root: {
    flexGrow: 1
  },
  menuButton: {
    marginRight: theme.spacing(2)
  },
  title: {
    flexGrow: 1,
    fontSize: '24px',
    textTransform: 'uppercase',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center'
  },
  appBarPosition: {
    left: '70px',
    backgroundColor: theme.palette.background.lighterBlue
  },
  tabRoot: {
    minWidth: '80px',
    fontSize: '14px',
    lineHeight: '18px',
    letterSpacing: '0px',
    color: theme.palette.grey.dark,
    opacity: '0.44',
    fontWeight: '600'
  },
  indicator: {
    height: '5px'
  },
  selected: {
    color: theme.palette.primary.main
  },
  iconStyle: {
    color: 'inherit',
    marginRight: '5px'
  },
  menuItem: {
    '& > .react-contexify__item__content > svg': {
      color: theme.palette.primary.main
    },
    '&:hover > .react-contexify__item__content  > svg': {
      color: '#fff'
    }
  },
  rootInput: {
    '& .MuiOutlinedInput-input': {
      padding: '15px 14px'
    }
  },
  toolbar: {
    minHeight: '50px'
  },
  buttonSpacing: {
    marginRight: '15px',
    [theme.breakpoints.down('sm')]: {
      marginRight: '8px'
    }
  },
  tabletFontSize: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.6rem'
    }
  }
}));
const TABS_CONTEXT_MENU_ID = 'TABS_CONTEXT_MENU_ID';

const AppBar = memo(
  ({
    title,
    tabs,
    dashboardData,
    isCanned,
    dashboardId,
    isLoading,
    isUserDashboard,
    setWhichPopup,
    setIsEditMode,
    isEditMode
  }) => {
    const classes = useStyles();

    const { dispatch, session } = useContext(UserSessionContext);
    const { homepagePreference, isInternalUser } = session;

    const history = useHistory();
    const { show } = useContextMenu();

    const [, sendRequest] = useApi();

    const theme = useTheme();
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('sm'));

    const location = useLocation();
    const { pathname } = location;

    const { masterObjectTypeId, objectTypeDetailId, masterObjectId } =
      isCanned && !!dashboardData && dashboardData;

    const isCannedDashboardTab = !!(isCanned && tabs.length > 0 && dashboardId);

    const currentDashboard = useMemo(() => {
      return tabs.find(tab => tab.id === dashboardId);
    }, [dashboardId, tabs]);

    const currentDashboardIndex = (currentDashboard && currentDashboard.ordinal) || 1;

    const [value, setValue] = useState();

    const displayMenu = useCallback(
      e => {
        const tabHref = e.currentTarget.href;
        const tabIdAndOrdinalPositionString = e.currentTarget.id;

        const tabProps = extractTabProps(tabIdAndOrdinalPositionString);

        show(e, {
          id: TABS_CONTEXT_MENU_ID,
          props: {
            masterObjectId,
            objectTypeDetailId,
            objectType: masterObjectTypeId,
            tabHref,
            ...tabProps
          }
        });
      },
      [masterObjectId, masterObjectTypeId, objectTypeDetailId, show]
    );

    const handleSetHomepageClick = useCallback(() => {
      sendRequest(UPDATE_USER_ROUTE(pathname));
      dispatch({ type: ACTIONS.UPDATE_USER_HOMEPAGE_PREFERENCE, payload: pathname });
    }, [dispatch, pathname, sendRequest]);

    useEffect(() => {
      if (isCannedDashboardTab) setValue(currentDashboardIndex);
    }, [currentDashboardIndex, isCannedDashboardTab]);

    const handleSearch = term => {
      history.push('/search-results', { term });
    };

    return (
      <div className={classes.root}>
        <MuiAppBar
          position="fixed"
          classes={{ positionFixed: classes.appBarPosition, root: classes.appRoot }}
        >
          <Toolbar className={classes.toolbar}>
            <Typography variant="h6" className={classes.title} color="primary">
              {isLoading || !title ? <Skeleton width="30%" height="40px" /> : title}
              {!isLoading && title && isUserDashboard && (
                <IconButton
                  style={{
                    marginLeft: '2px',
                    marginBottom: '20px'
                  }}
                  onClick={() => setIsEditMode(prevState => !prevState)}
                >
                  <EditIcon
                    style={{
                      fontSize: '16px',
                      color: '#000'
                    }}
                  />
                </IconButton>
              )}
            </Typography>
            {isEditMode && (
              <>
                {homepagePreference !== pathname && (
                  <Button
                    variant="outlined"
                    color="primary"
                    className={classNames(classes.buttonSpacing, classes.tabletFontSize)}
                    onClick={handleSetHomepageClick}
                  >
                    {isSmallerThanMd ? 'Set Homepage' : 'Set As Homepage'}
                  </Button>
                )}

                <Button
                  variant="outlined"
                  color="primary"
                  className={classNames(classes.buttonSpacing, classes.tabletFontSize)}
                  onClick={() => setWhichPopup('dashboard_share')}
                >
                  {isSmallerThanMd ? 'Share' : 'Share Dashboard'}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  className={classNames(classes.buttonSpacing, classes.tabletFontSize)}
                  onClick={() => setWhichPopup('dashboard_edit')}
                >
                  {isSmallerThanMd ? 'Edit' : 'Edit Dashboard'}
                </Button>
                <Button
                  variant="outlined"
                  className={classNames(classes.buttonSpacing, classes.tabletFontSize)}
                  style={{ color: '#E60000' }}
                  onClick={() => setWhichPopup('dashboard_delete')}
                >
                  {isSmallerThanMd ? 'Delete' : 'Delete Dashboard'}
                </Button>
              </>
            )}
            <SearchInput onSearch={handleSearch} />
          </Toolbar>
          {isCanned && (
            <>
              <Divider />
              <Tabs
                value={value}
                classes={{ indicator: classes.indicator, flexContainer: classes.flexContainer }}
              >
                {dashboardData &&
                  tabs &&
                  tabs.map(tab => (
                    tab.isExternal === null || tab.isExternal === isInternalUser ?
                    <Tab
                      id={`${tab.id}@${tab.ordinal}`}
                      onContextMenu={displayMenu}
                      label={tab.name}
                      value={tab.ordinal}
                      component={Link}
                      to={`/canned/${dashboardData.masterObjectId}/${tab.id}`}
                      key={tab.name}
                      classes={{
                        root: classes.tabRoot,
                        selected: classes.selected
                      }}
                      disableRipple
                      /> : null
                  ))}
              </Tabs>
            </>
          )}
        </MuiAppBar>
      </div>
    );
  }
);

AppBar.propTypes = {
  isUserDashboard: PropTypes.bool,
  setWhichPopup: PropTypes.func,
  setIsEditMode: PropTypes.func,
  isEditMode: PropTypes.bool,
  title: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isCanned: PropTypes.bool,
  dashboardData: PropTypes.shape({
    masterObjectId: PropTypes.number,
    masterObjectName: PropTypes.string,
    masterObjectType: PropTypes.string
  }),
  dashboardId: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      isDefaultScreen: PropTypes.bool.isRequired,
      name: PropTypes.string.isRequired,
      ordinal: PropTypes.number.isRequired,
      isExternal: PropTypes.bool.isRequired
    })
  )
};

AppBar.defaultProps = {
  isCanned: false,
  title: null,
  dashboardData: {},
  tabs: [],
  dashboardId: null,
  isUserDashboard: false,
  setWhichPopup: null,
  setIsEditMode: null,
  isEditMode: false
};

export default AppBar;
